import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProfileStore } from '../../../state/profileStore';
import PasswordStrengthMeter, { MIN_LENGTH } from '../PasswordStrengthMeter';


const CreatePasswordFlow = () => {
  const { login } = useProfileStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const params = new URLSearchParams(location.search);
  const token = params.get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (password.length < MIN_LENGTH) {
      setError(`Password must be at least ${MIN_LENGTH} characters`);
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/complete-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
        credentials: 'include'
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Password update failed');
      }

      const loginResult = await login(data.u_email, password);
      if (!loginResult?.success) {
        throw new Error('Login failed');
      }

      setTimeout(() => {
        navigate('/profile');
      }, 1000);

    } catch (err) {
      console.error('Password flow error:', err);
      setError(err.message || 'Unexpected error');
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-20 p-6 bg-white shadow rounded">
      <h2 className="text-2xl font-bold mb-4 text-center">Create Your Password</h2>
      {error && <p className="text-red-500 text-center mb-2">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="New Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full p-2 border rounded pr-10"
            required
            minLength={MIN_LENGTH}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2 top-2 text-gray-500"
          >
            {showPassword ? "\ud83d\udc41\ufe0f" : "\ud83d\udc41\ufe0f\u200d\ud83d\udde8\ufe0f"}
          </button>
        </div>
        <PasswordStrengthMeter password={password} />
        <div className="relative">
          <input
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full p-2 border rounded pr-10"
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-2 top-2 text-gray-500"
          >
            {showConfirmPassword ? "\ud83d\udc41\ufe0f" : "\ud83d\udc41\ufe0f\u200d\ud83d\udde8\ufe0f"}
          </button>
        </div>
        <button
          type="submit"
          className="w-full bg-[#00aef7] text-white py-2 rounded hover:bg-[#00aef7]"
          disabled={isLoading}
        >
          {isLoading ? 'Creating...' : 'Set Password and Login'}
        </button>
      </form>
    </div>
  );
};

export default CreatePasswordFlow;
